import { useEffect, useState } from 'react';
import type { StepProps } from '../engine/context.ts';
import { TitleBlock } from '../ui/TitleBlock.tsx';
import { Icon } from '../ui/Icon.tsx';
import { CORAL, CORAL_DEEP, CREAM, INK, LINE, MUTED, ORANGE, PAPER, SHADOW_CARD } from '../ui/theme.ts';

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** email → POST /lead → advance to the plan loader. */
export function Email({ step, content, ctx }: StepProps) {
  const key = step.answerKey ?? 'email';
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const valid = EMAIL_RE.test(value);
  const typo = touched && !valid && value.length > 0;
  const shared = ctx.shared.email;

  useEffect(() => {
    ctx.setCta({
      label: busy ? '…' : (content.cta ?? 'Generate my plan'),
      caption: content.ctaCaption,
      icon: content.ctaIcon,
      large: true,
      disabled: !valid || busy,
      spinner: busy,
      onPress: async () => {
        if (!valid || busy) return;
        setBusy(true);
        setFailed(false);
        try {
          ctx.setAnswer(key, { value });
          await ctx.submitLead(value);
          ctx.next();
        } catch {
          setFailed(true);
        } finally {
          setBusy(false);
        }
      },
    });
  }, [step.id, value, valid, busy, content.cta]);

  return (
    <>
      <TitleBlock label={content.label} title={content.title} highlight={content.highlight} subtitle={content.subtitle} />

      {typo || failed ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '12px 14px', borderRadius: 16, background: CREAM, fontSize: 13, fontWeight: 700 }}>
          <Icon name="warning-circle" size={20} color={CORAL_DEEP} />
          {typo ? shared.errorText : (shared.submitErrorText ?? 'We could not save your plan just now. Please try again.')}
        </div>
      ) : null}

      <div style={{ position: 'relative' }}>
        <Icon name="envelope-simple" size={22} color={MUTED} style={{ position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)' }} />
        <input
          className="fk-input"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={value}
          aria-label="Email"
          aria-invalid={typo}
          placeholder={shared.placeholder}
          onChange={(e) => {
            setValue(e.target.value);
            setTouched(false);
            setFailed(false);
          }}
          onBlur={() => setTouched(true)}
          style={{
            width: '100%',
            minHeight: 60,
            padding: '12px 18px 12px 50px',
            borderRadius: 18,
            font: 'inherit',
            fontWeight: 600,
            fontSize: 16,
            background: PAPER,
            color: INK,
            border: `2px solid ${typo ? CORAL : valid ? ORANGE : LINE}`,
            boxShadow: SHADOW_CARD,
          }}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 'var(--fk-card-pad)', borderRadius: 20, background: CREAM }}>
        {shared.trust.map((t) => (
          <div key={t.title} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ width: 36, height: 36, flex: 'none', borderRadius: 12, background: PAPER, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: CORAL_DEEP }}>
              <Icon name={t.icon} size={20} />
            </span>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              <div style={{ fontWeight: 800, fontSize: 15 }}>{t.title}</div>
              <div style={{ color: MUTED }}>{t.body}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
        {shared.badges.map((b) => (
          <span
            key={b.label}
            style={{
              display: 'inline-flex',
              gap: 6,
              alignItems: 'center',
              padding: '6px 12px',
              borderRadius: 999,
              background: PAPER,
              border: `2px solid ${LINE}`,
              fontSize: 12,
              fontWeight: 800,
              color: INK,
            }}
          >
            <Icon name={b.icon} size={16} color={CORAL_DEEP} />
            {b.label}
          </span>
        ))}
      </div>
    </>
  );
}
